/**
 * Per-request timeout budget for `useFetchBase`.
 *
 * Replaces the previous single blunt 15s `AbortSignal.timeout` (PR #1863) that
 * was applied to every request regardless of method or environment. That fixed
 * deadline aborted legitimately slow requests in two ways:
 *
 *   1. It cut off non-idempotent charging POSTs (e.g. `process-checkout`)
 *      mid-flight. Unlike safe GETs, these are NOT auto-retried, and aborting
 *      one turns a possibly-successful charge into an ambiguous client failure
 *      that nudges the user into a manual retry.
 *   2. It fired during the normal (slow) lifetime of requests inside FB/IG
 *      in-app webviews, which throttle JS + network far below a standalone
 *      browser.
 *
 * The budget is therefore tuned along two axes:
 *   • HTTP method — safe methods stay tightly bounded (an early abort is cheap
 *     because they auto-retry); unsafe methods get a fixed, more generous
 *     deadline.
 *   • Environment — in-app webviews scale up the SAFE budget only. The unsafe
 *     budget is already a generous ecommerce-standard 30s ceiling; doubling it
 *     in a webview would just mean a longer dead "Processing" spinner without
 *     making an ambiguous charge any less ambiguous.
 */

import { isSafeHttpMethod } from "./vercel-proxy";
import { isInAppWebview } from "./detect-webview";

// Safe (idempotent, read-only) methods: bounded tightly. `useFetchBase`
// auto-retries transient failures for these, so an early timeout is cheap and
// keeps the UI responsive instead of waiting on a hung upstream.
export const SAFE_METHOD_TIMEOUT_MS = 15_000;

// Unsafe (side-effecting / non-idempotent) methods: the charging checkout POST
// lives here. These are never auto-retried, so aborting one creates an
// ambiguous outcome (did the backend charge or not?). A generous deadline
// avoids cutting off a request the backend is still processing, while still
// bounding a genuinely hung upstream so the UI can surface a definite result.
// 30s is the common ecommerce / payment-gateway ceiling and is plenty even on
// a slow webview — so this is a flat ceiling, NOT scaled by the webview factor.
export const UNSAFE_METHOD_TIMEOUT_MS = 30_000;

// In-app webviews (FB/IG/Messenger) throttle JS + network, so widen the SAFE
// budget rather than aborting GETs that are merely slow, not stuck. Applied to
// safe methods only (see `resolveRequestTimeoutMs`).
export const IN_APP_WEBVIEW_TIMEOUT_MULTIPLIER = 2;

/**
 * Resolves the abort deadline (ms) for a request given its HTTP method and the
 * current user-agent. Pure so it is unit-testable without mocking globals.
 */
export function resolveRequestTimeoutMs(
    method: string | undefined,
    userAgent: string | null | undefined,
): number {
    // Unsafe (charging) methods: flat 30s ceiling regardless of environment.
    if (!isSafeHttpMethod(method)) {
        return UNSAFE_METHOD_TIMEOUT_MS;
    }

    // Safe (read-only, auto-retried) methods: tight by default, widened in
    // in-app webviews where 15s demonstrably aborts legitimately slow GETs.
    return isInAppWebview(userAgent)
        ? SAFE_METHOD_TIMEOUT_MS * IN_APP_WEBVIEW_TIMEOUT_MULTIPLIER
        : SAFE_METHOD_TIMEOUT_MS;
}
